import React from 'react';
import { useTranslation } from 'react-i18next';
import type { StructuredResultItem } from '../types';
import { formatDistance } from '../utils';

interface GenericResultItemRendererProps {
  item: StructuredResultItem;
}

export const GenericResultItemRenderer: React.FC<GenericResultItemRendererProps> = ({ item }) => {
  const { t } = useTranslation();

  const renderContentSpecificInfo = () => {
    const stats = [];
    
    // Trail-specific information
    if (item.content_type === 'bike_trail' || item.content_type === 'hiking_trail') {
      if (item.distance_km) stats.push(`🏃‍♂️ ${formatDistance(item.distance_km)} km`);
      if (item.elevation_gain) stats.push(`⛰️ ${item.elevation_gain} m`);
    }
    
    // Event-specific information
    if (item.content_type === 'event') {
      if (item.event_date) stats.push(`📅 ${item.event_date}`);
      if (item.event_time) stats.push(`🕐 ${item.event_time}`);
      if (item.venue) stats.push(`🏢 ${item.venue}`);
    }
    
    // Attraction-specific information
    if (item.content_type === 'attraction') {
      if (item.attraction_type) stats.push(`🏛️ ${item.attraction_type}`);
      if (item.opening_hours) stats.push(`🕐 ${item.opening_hours}`);
    }
    
    // Common location info
    stats.push(`📍 ${item.location}`);
    if (item.distance_from_user !== undefined && item.distance_from_user !== null) {
      stats.push(`📏 ${formatDistance(item.distance_from_user)} km ${t('results.distance.away')}`);
    }
    
    return stats;
  };

  const getBadgeInfo = () => {
    if (item.difficulty_level) {
      return { text: item.difficulty_level, color: 'bg-green-100 text-green-800' };
    }
    if (item.attraction_type) {
      return { text: item.attraction_type, color: 'bg-blue-100 text-blue-800' };
    }
    if (item.content_type === 'event') {
      return { text: t('results.contentTypes.event'), color: 'bg-purple-100 text-purple-800' };
    }
    return null;
  };

  const badge = getBadgeInfo();

  return (
    <div className="border rounded p-3 bg-white text-sm">
      {/* Title and Badge */}
      <div className="flex items-start justify-between mb-2">
        <h5 className="font-semibold text-gray-900 leading-tight flex-1 pr-2">{item.title}</h5>
        {badge && (
          <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${badge.color}`}>
            {badge.text}
          </span>
        )}
      </div>
      
      {/* Content-specific stats */}
      <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-600">
        {renderContentSpecificInfo().map((stat, index) => (
          <span key={index}>{stat}</span>
        ))}
      </div>
      
      {/* Description */}
      <p className="text-gray-700 mb-2 text-sm leading-tight">{item.description}</p>
      
      {/* Link */}
      <a 
        href={item.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="inline-flex items-center text-blue-600 hover:text-blue-800 text-xs font-medium"
      >
        🔗 {t('results.actions.visitWebsite')}
      </a>
    </div>
  );
};